import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import api, { getUser } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Trophy, Users, Plus, Trash2, Calendar, MapPin, FolderOpen, Lock, Unlock, ImageIcon, ListChecks, BarChart3, Pencil } from "lucide-react";
import { toast } from "sonner";
import PartnersPanel from "../components/PartnersPanel";
import SubmissionsPanel from "../components/SubmissionsPanel";
import StatsByDisciplinePanel from "../components/StatsByDisciplinePanel";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [tab, setTab] = useState("competitions");

  useEffect(() => {
    if (!user) {
      navigate("/admin/login");
      return;
    }
    if (user.role !== "admin") {
      navigate("/admin/dj");
    }
  }, [user, navigate]);

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-white/40">Dashboard</div>
          <h1 className="text-4xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-[#FF5722] to-[#00E5FF] bg-clip-text text-transparent">
              Admin
            </span>
          </h1>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-[#0E0E11] border border-white/10 rounded-full p-1.5 flex-wrap h-auto">
            <TabsTrigger
              value="competitions"
              data-testid="tab-competitions"
              className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF5722] data-[state=active]:to-[#00E5FF] data-[state=active]:text-white px-5"
            >
              <Trophy className="w-4 h-4 mr-2" /> Competizioni
            </TabsTrigger>
            <TabsTrigger
              value="djs"
              data-testid="tab-djs"
              className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF5722] data-[state=active]:to-[#00E5FF] data-[state=active]:text-white px-5"
            >
              <Users className="w-4 h-4 mr-2" /> DJ Responsabili
            </TabsTrigger>
            <TabsTrigger
              value="submissions"
              data-testid="tab-submissions"
              className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF5722] data-[state=active]:to-[#00E5FF] data-[state=active]:text-white px-5"
            >
              <ListChecks className="w-4 h-4 mr-2" /> Invii & Statistiche
            </TabsTrigger>
            <TabsTrigger
              value="partners"
              data-testid="tab-partners"
              className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF5722] data-[state=active]:to-[#00E5FF] data-[state=active]:text-white px-5"
            >
              <ImageIcon className="w-4 h-4 mr-2" /> Partner Loghi
            </TabsTrigger>
            <TabsTrigger
              value="stats"
              data-testid="tab-stats"
              className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF5722] data-[state=active]:to-[#00E5FF] data-[state=active]:text-white px-5"
            >
              <BarChart3 className="w-4 h-4 mr-2" /> Statistiche
            </TabsTrigger>
          </TabsList>

          <TabsContent value="competitions">
            <CompetitionsPanel />
          </TabsContent>
          <TabsContent value="djs">
            <DJsPanel />
          </TabsContent>
          <TabsContent value="submissions">
            <SubmissionsPanel />
          </TabsContent>
          <TabsContent value="partners">
            <PartnersPanel />
          </TabsContent>
          <TabsContent value="stats">
            <StatsByDisciplinePanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function CompetitionsPanel() {
  const navigate = useNavigate();
  const [comps, setComps] = useState([]);
  const [djs, setDjs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyForm = { name: "", location: "", start_date: "", end_date: "", dj_ids: [] };
  const [form, setForm] = useState(emptyForm);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      name: c.name,
      location: c.location,
      start_date: c.start_date,
      end_date: c.end_date,
      dj_ids: c.dj_ids || [],
    });
    setOpen(true);
  };

  const reload = () => {
    api.get("/competitions/all").then((r) => setComps(r.data));
    api.get("/dj").then((r) => setDjs(r.data));
  };
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDj = (id) =>
    setForm((f) => ({
      ...f,
      dj_ids: f.dj_ids.includes(id) ? f.dj_ids.filter((x) => x !== id) : [...f.dj_ids, id],
    }));

  const create = async () => {
    if (!form.name || !form.location || !form.start_date || !form.end_date) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (form.dj_ids.length === 0) {
      toast.error("Seleziona almeno un DJ Responsabile");
      return;
    }
    if (form.start_date > form.end_date) {
      toast.error("La data di fine deve essere uguale o successiva all'inizio");
      return;
    }
    try {
      if (editId) {
        await api.put(`/competitions/${editId}`, form);
        toast.success("Competizione aggiornata");
      } else {
        await api.post("/competitions", form);
        toast.success("Competizione creata");
      }
      setOpen(false);
      setEditId(null);
      setForm(emptyForm);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la competizione e TUTTI i file?")) return;
    try {
      await api.delete(`/competitions/${id}`);
      toast.success("Eliminata");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const toggleClose = async (c) => {
    try {
      const r = await api.patch(`/competitions/${c.id}/toggle-close`);
      toast.success(r.data.closed ? "Competizione chiusa" : "Competizione riaperta");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div className="text-white/60 text-sm">{comps.length} competizioni totali</div>
        <Button
          data-testid="create-competition-btn"
          onClick={openCreate}
          className="rounded-full bg-gradient-to-r from-[#FF5722] to-[#00E5FF] border-0"
        >
          <Plus className="w-4 h-4 mr-1" /> Nuova Competizione
        </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="comp-dialog" className="bg-[#0E0E11] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>{editId ? "Modifica Competizione" : "Crea Competizione"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Nome *</Label>
                <Input
                  data-testid="comp-name-input"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className="mt-1 bg-black/60 border-white/10"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60">Luogo *</Label>
                <Input
                  data-testid="comp-location-input"
                  value={form.location}
                  onChange={(e) => setField("location", e.target.value)}
                  className="mt-1 bg-black/60 border-white/10"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/60">Inizio *</Label>
                  <Input
                    data-testid="comp-start-input"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setField("start_date", e.target.value)}
                    className="mt-1 bg-black/60 border-white/10"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/60">Fine *</Label>
                  <Input
                    data-testid="comp-end-input"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setField("end_date", e.target.value)}
                    className="mt-1 bg-black/60 border-white/10"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-white/60 mb-2 inline-block">
                  DJ Responsabili Musiche *
                </Label>
                {djs.length === 0 ? (
                  <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    Nessun DJ creato. Crea prima un DJ Responsabile nella scheda DJ.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-44 overflow-auto bg-black/40 rounded-lg p-3 border border-white/10">
                    {djs.map((d) => (
                      <label
                        key={d.id}
                        className="flex items-center gap-2 cursor-pointer hover:bg-white/5 px-2 py-1 rounded"
                      >
                        <Checkbox
                          checked={form.dj_ids.includes(d.id)}
                          onCheckedChange={() => toggleDj(d.id)}
                          data-testid={`comp-dj-checkbox-${d.id}`}
                        />
                        <span className="text-sm">
                          {d.nome} {d.cognome}{" "}
                          <span className="text-white/40 text-xs">({d.email})</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Annulla
              </Button>
              <Button
                data-testid="comp-save-btn"
                onClick={create}
                className="bg-gradient-to-r from-[#FF5722] to-[#00E5FF] border-0"
              >
                {editId ? "Salva modifiche" : "Crea"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {comps.map((c) => (
          <div
            key={c.id}
            data-testid={`admin-comp-${c.id}`}
            className="bg-[#0E0E11] rounded-2xl p-5 relative overflow-hidden border border-white/10"
            style={{ borderTop: `4px solid ${c.border_color}` }}
          >
            <div
              className="absolute -top-14 -right-14 w-32 h-32 rounded-full blur-3xl opacity-25"
              style={{ background: c.border_color }}
            />
            <h3 className="font-bold text-lg relative">{c.name}</h3>
            <div className="space-y-1.5 text-sm text-white/70 mt-2 relative">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5" style={{ color: c.border_color }} />
                {c.location}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5" style={{ color: c.border_color }} />
                {c.start_date}
                {c.end_date !== c.start_date && ` → ${c.end_date}`}
              </div>
              {c.dj_names?.length > 0 && (
                <div className="text-xs text-white/50">DJ: {c.dj_names.join(", ")}</div>
              )}
              <div className="flex items-center gap-2 mt-2">
                {c.closed ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    <Lock className="w-3 h-3" /> Chiusa
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    <Unlock className="w-3 h-3" /> Aperta
                  </span>
                )}
                {c.submission_count != null && (
                  <span className="text-xs text-white/50">
                    {c.submission_count} invii
                  </span>
                )}
              </div>
            </div>
            <div className="mt-4 flex gap-2 relative">
              <Button
                size="sm"
                onClick={() => navigate(`/admin/competizioni/${c.id}/files`)}
                data-testid={`browse-comp-${c.id}`}
                className="rounded-full text-xs flex-1 border-0 text-white"
                style={{
                  background: `linear-gradient(135deg, ${c.border_color} 0%, #00E5FF 100%)`,
                }}
              >
                <FolderOpen className="w-3 h-3 mr-1" /> Sfoglia File
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openEdit(c)}
                data-testid={`edit-comp-${c.id}`}
                className="rounded-full hover:bg-cyan-500/20 text-cyan-300"
                title="Modifica"
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => toggleClose(c)}
                data-testid={`toggle-close-${c.id}`}
                className="rounded-full hover:bg-white/10 text-white/70"
                title={c.closed ? "Riapri" : "Chiudi"}
              >
                {c.closed ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => remove(c.id)}
                data-testid={`delete-comp-${c.id}`}
                className="rounded-full hover:bg-red-500/20 text-red-300"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {comps.length === 0 && (
          <div className="col-span-full bg-white/5 border border-white/10 rounded-2xl p-10 text-center text-white/60">
            Nessuna competizione creata
          </div>
        )}
      </div>
    </div>
  );
}

function DJsPanel() {
  const [djs, setDjs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", password: "" });

  const reload = () => api.get("/dj").then((r) => setDjs(r.data));
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!form.nome || !form.cognome || !form.email || !form.password) {
      toast.error("Tutti i campi sono obbligatori");
      return;
    }
    try {
      await api.post("/dj", form);
      toast.success("DJ creato");
      setOpen(false);
      setForm({ nome: "", cognome: "", email: "", password: "" });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo DJ?")) return;
    await api.delete(`/dj/${id}`);
    toast.success("Eliminato");
    reload();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div className="text-white/60 text-sm">{djs.length} DJ Responsabili</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-dj-btn"
              className="rounded-full bg-gradient-to-r from-[#FF5722] to-[#00E5FF] border-0"
            >
              <Plus className="w-4 h-4 mr-1" /> Nuovo DJ
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0E0E11] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Crea DJ Responsabile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                data-testid="dj-nome-input"
                placeholder="Nome"
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                className="bg-black/60 border-white/10"
              />
              <Input
                data-testid="dj-cognome-input"
                placeholder="Cognome"
                value={form.cognome}
                onChange={(e) => setForm((f) => ({ ...f, cognome: e.target.value }))}
                className="bg-black/60 border-white/10"
              />
              <Input
                data-testid="dj-email-input"
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="bg-black/60 border-white/10"
              />
              <Input
                data-testid="dj-password-input"
                placeholder="Password (min 8 + 1 simbolo)"
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="bg-black/60 border-white/10"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Annulla
              </Button>
              <Button
                data-testid="dj-save-btn"
                onClick={create}
                className="bg-gradient-to-r from-[#FF5722] to-[#00E5FF] border-0"
              >
                Crea
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#0E0E11] border border-white/10 rounded-2xl overflow-hidden">
        {djs.length === 0 ? (
          <div className="p-10 text-center text-white/60">Nessun DJ creato</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-3 font-medium">Nome</th>
                <th className="text-left p-3 font-medium">Cognome</th>
                <th className="text-left p-3 font-medium">Email</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {djs.map((d) => (
                <tr key={d.id} className="border-t border-white/10" data-testid={`dj-row-${d.id}`}>
                  <td className="p-3">{d.nome}</td>
                  <td className="p-3">{d.cognome}</td>
                  <td className="p-3 text-white/70">{d.email}</td>
                  <td className="p-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(d.id)}
                      data-testid={`delete-dj-${d.id}`}
                      className="hover:bg-red-500/20 text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
